import type { Attachment, DeviceOs } from '@/signalr';

export type IncidentDetailDialogRequest =
  | { kind: 'phoneNumber'; incidentId: string }
  | { kind: 'shareByLink'; incidentId: string }
  | { kind: 'addAgencyToIncident'; incidentId: string }
  | { kind: 'manualGpsCoordinates'; incidentId: string }
  | { kind: 'editCallerName'; incidentId: string; currentValue: string | null }
  | { kind: 'editDispatcherNote'; incidentId: string; currentValue: string | null }
  | { kind: 'editCallSign'; incidentId: string; currentValue: string | null }
  | { kind: 'editIncidentLabel'; incidentId: string; currentValue: string | null }
  | { kind: 'editPatientDescription'; incidentId: string; currentValue: string | null }
  | {
      kind: 'confirmOrRejectIncidentShare';
      incidentId: string;
      onResult: (confirmed: boolean) => Promise<void>;
    }
  | { kind: 'howToTurnOnGps'; deviceOs: DeviceOs }
  | { kind: 'mediaViewer'; media: readonly Attachment[]; startIndex: number };

type DialogRequestListener = (request: IncidentDetailDialogRequest) => void;

let listener: DialogRequestListener | null = null;

export function subscribeIncidentDetailDialogRequests(newListener: DialogRequestListener): () => void {
  listener = newListener;
  return () => {
    if (listener === newListener) {
      listener = null;
    }
  };
}

function openDialog(request: IncidentDetailDialogRequest): void {
  if (listener === null) {
    console.warn('[incident-detail] No dialog host is mounted; dialog request dropped.', request);
    return;
  }
  listener(request);
}

export function openPhoneNumberDialog(incidentId: string): void {
  openDialog({ kind: 'phoneNumber', incidentId });
}

export function openShareByLinkDialog(incidentId: string): void {
  openDialog({ kind: 'shareByLink', incidentId });
}

export function openAddAgencyToIncidentDialog(incidentId: string): void {
  openDialog({ kind: 'addAgencyToIncident', incidentId });
}

export function openManualGpsCoordinatesDialog(incidentId: string): void {
  openDialog({ kind: 'manualGpsCoordinates', incidentId });
}

export function openEditCallerNameDialog(incidentId: string, currentValue: string | null): void {
  openDialog({ kind: 'editCallerName', incidentId, currentValue });
}

export function openEditDispatcherNoteDialog(incidentId: string, currentValue: string | null): void {
  openDialog({ kind: 'editDispatcherNote', incidentId, currentValue });
}

export function openEditCallSignDialog(incidentId: string, currentValue: string | null): void {
  openDialog({ kind: 'editCallSign', incidentId, currentValue });
}

export function openEditIncidentLabelDialog(incidentId: string, currentValue: string | null): void {
  openDialog({ kind: 'editIncidentLabel', incidentId, currentValue });
}

export function openEditPatientDescriptionDialog(incidentId: string, currentValue: string | null): void {
  openDialog({ kind: 'editPatientDescription', incidentId, currentValue });
}

export function openConfirmOrRejectIncidentShareDialog(
  incidentId: string,
  onResult: (confirmed: boolean) => Promise<void>,
): void {
  openDialog({ kind: 'confirmOrRejectIncidentShare', incidentId, onResult });
}

export function openHowToTurnOnGpsDialog(deviceOs: DeviceOs): void {
  openDialog({ kind: 'howToTurnOnGps', deviceOs });
}

export function openMediaViewerDialog(media: readonly Attachment[], startIndex: number): void {
  openDialog({ kind: 'mediaViewer', media, startIndex });
}
